import { useState, FormEvent } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from '../../../i18n/translations';

export default function ContactCTA() {
  const { language } = useLanguage();
  const t = translations[language].contactCTA;

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isHuman, setIsHuman] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isHuman) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6g6i01ghdq4qda702ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', service: '', message: '' });
        setIsHuman(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A2332] via-[#243447] to-[#2D1B4E]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
            {t.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Left: Contact Info & Map */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6">{t.contactInfo.title}</h3>
              <div className="space-y-5">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00A3FF]/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-[#00A3FF]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">{t.contactInfo.address}</div>
                    <div className="text-white leading-relaxed">
                      {t.contactInfo.addressValue}
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00A3FF]/20 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-[#00A3FF]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">{t.contactInfo.phone}</div>
                    <a href="tel:+85235795580" className="text-white hover:text-[#00A3FF] transition-colors cursor-pointer">
                      (852) 3579 5580
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00A3FF]/20 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-[#00A3FF]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">{t.contactInfo.email}</div>
                    <a href="mailto:info@ibushk.com" className="text-white hover:text-[#00A3FF] transition-colors cursor-pointer">
                      info@ibushk.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-2 border border-white/20 overflow-hidden">
              <div className="w-full h-80 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.8976543210123!2d114.14938954703736!3d22.285821507890617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404007c8a9e5555%3A0x1234567890abcdef!2z6YeR54WM6KGM!5e0!3m2!1szh-TW!2shk!4v1700000000000!5m2!1szh-TW!2shk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="IBUS (HK) LTD Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-10 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-6">{t.form.title}</h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">{t.form.name} *</label>
                  <input
                    type="text" id="name" name="name" required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent text-sm"
                    placeholder={t.form.namePlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-white mb-2">{t.form.company} *</label>
                  <input
                    type="text" id="company" name="company" required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent text-sm"
                    placeholder={t.form.companyPlaceholder}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">{t.form.email} *</label>
                  <input
                    type="email" id="email" name="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent text-sm"
                    placeholder={t.form.emailPlaceholder}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">{t.form.phone} *</label>
                  <input
                    type="tel" id="phone" name="phone" required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent text-sm"
                    placeholder={t.form.phonePlaceholder}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-white mb-2">{t.form.service} *</label>
                <select
                  id="service" name="service" required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent cursor-pointer text-sm"
                >
                  <option value="" className="bg-gray-800">{t.form.servicePlaceholder}</option>
                  <option value={t.form.serviceOptions.cabling} className="bg-gray-800">{t.form.serviceOptions.cabling}</option>
                  <option value={t.form.serviceOptions.infrastructure} className="bg-gray-800">{t.form.serviceOptions.infrastructure}</option>
                  <option value={t.form.serviceOptions.managed} className="bg-gray-800">{t.form.serviceOptions.managed}</option>
                  <option value={t.form.serviceOptions.comprehensive} className="bg-gray-800">{t.form.serviceOptions.comprehensive}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">{t.form.message}</label>
                <textarea
                  id="message" name="message" rows={4} maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent resize-none text-sm"
                  placeholder={t.form.messagePlaceholder}
                ></textarea>
                <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
              </div>

              {/* 真人驗證 */}
              <div
                className={`flex items-center justify-between px-5 py-4 rounded-lg border ${captchaError ? 'border-red-400/70 bg-red-500/10' : 'border-white/20 bg-white/5'} transition-colors`}
              >
                <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => { setIsHuman(!isHuman); setCaptchaError(false); }}>
                  {/* 自訂勾選框 */}
                  <div className={`w-6 h-6 flex items-center justify-center rounded border-2 transition-all duration-200 ${isHuman ? 'bg-[#00A3FF] border-[#00A3FF]' : captchaError ? 'border-red-400 bg-transparent' : 'border-white/50 bg-transparent'} cursor-pointer`}>
                    {isHuman && (
                      <svg viewBox="0 0 12 10" className="w-3.5 h-3.5" fill="none">
                        <polyline points="1,5 4.5,8.5 11,1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${captchaError ? 'text-red-300' : 'text-gray-200'}`}>
                    {t.form.captcha}
                  </span>
                </div>
                {/* reCAPTCHA 樣式標誌 */}
                <div className="flex flex-col items-center opacity-60">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-gray-300"></i>
                  </div>
                  <span className="text-[9px] text-gray-400 mt-0.5 leading-tight text-center">reCAPTCHA<br/>{language === 'zh' ? '驗證' : 'Verify'}</span>
                </div>
              </div>
              {captchaError && (
                <p className="text-red-400 text-xs -mt-3">{t.form.captchaError}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#FF6B35] text-white text-base font-semibold rounded-full hover:bg-[#E55A24] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? t.form.submitting : t.form.submit}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 text-sm text-center">
                  {t.form.successMessage}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm text-center">
                  {t.form.errorMessage}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Equipment Icons */}
        <div className="mt-16 flex items-center justify-center space-x-12 opacity-40">
          <div className="text-center">
            <i className="ri-server-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.server}</div>
          </div>
          <div className="text-center">
            <i className="ri-router-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.router}</div>
          </div>
          <div className="text-center">
            <i className="ri-database-2-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.storage}</div>
          </div>
          <div className="text-center">
            <i className="ri-shield-check-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.firewall}</div>
          </div>
          <div className="text-center">
            <i className="ri-phone-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.voip}</div>
          </div>
          <div className="text-center">
            <i className="ri-cloud-line text-5xl text-white mb-2"></i>
            <div className="text-xs text-white">{t.equipment.cloud}</div>
          </div>
        </div>
      </div>
    </section>
  );
}